/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Spin, Button, Input } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadRtoWithReasonReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";

const RtoWithReasonReport = () => {
    // Set default date range to yesterday and today
    const defaultDateRange = [
        moment().subtract(1, "days"),
        moment()
    ];

    const [loading, setLoading] = useState(false);
    const [hubListLoading, setHubListLoading] = useState(false);
    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [operationDateRange, setOperationDateRange] = useState(null);
    const [hubList, setHubList] = useState([]);

    // Filters
    const [returnPremiseIds, setReturnPremiseIds] = useState([]);
    const [currentPremiseIds, setCurrentPremiseIds] = useState([]);
    const [srcHubIds, setSrcHubIds] = useState([]);
    const [headerHubList, setHeaderHubList] = useState([]);
    const [hubUser, setHubUser] = useState();


    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for RTO Shipment with Reason Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);
    const [destinationHubIds, setDestinationHubIds] = useState([]);
    const [docTypes, setDocTypes] = useState([]);
    const [bookingTypes, setBookingTypes] = useState([]);
    const [awbNumbers, setAwbNumbers] = useState("");

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    // Sync with App Header hub changes
    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
            const role = user?.roles?.[0];
            const isSpecialRole = role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR";

            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setCurrentPremiseIds([newActiveHubId]);
            } else if (isSpecialRole) {
                setCurrentPremiseIds([]);
            } else {
                setCurrentPremiseIds(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setHubListLoading(true);
                const hubs = await getHubList();

                // Clean up hub labels if they contain the ID (e.g. "123 - Hub Name" -> "Hub Name")
                const cleanedHubs = hubs.map(hub => {
                    const id = String(hub.value);
                    const label = String(hub.label);
                    // Check if label starts with ID, followed by optional separator
                    if (label.startsWith(id)) {
                        // Remove ID and any following separator (space, hyphen, colon)
                        const newLabel = label.replace(new RegExp(`^${id}\\s*[-:]?\\s*`), "");
                        return { ...hub, label: newLabel || label };
                    }
                    return hub;
                });

                setHubList(cleanedHubs);

                if (!hubInitializedRef.current) {
                    const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
                    const userHubs = userFullData?.userInfo?.hubDetails || [];
                    const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
                    const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";
                    const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
                    const role = user?.roles?.[0];
                    setHubUser(role);

                    if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                        setCurrentPremiseIds([currentActiveHubId]);
                    } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                        setCurrentPremiseIds([]);
                    } else {
                        setCurrentPremiseIds(["ALL"]);
                    }
                    hubInitializedRef.current = true;
                }
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                setHubListLoading(false);
                setIsInitialized(true);
            }
        })();
    }, [headerHubList]);

    // Fetch report data when filters or pagination change
    useEffect(() => {
        // Only fetch if date range is present and component is initialized
        if (!isInitialized) return;

        const bookingRangeSet = dateRange && dateRange[0] && dateRange[1];
        const operationRangeSet = operationDateRange && operationDateRange[0] && operationDateRange[1];

        // Prevent calls if user is still picking (one date selected, the other null)
        const isBookingMidSelection = dateRange && (!!dateRange[0] !== !!dateRange[1]);
        const isOperationMidSelection = operationDateRange && (!!operationDateRange[0] !== !!operationDateRange[1]);

        if ((bookingRangeSet || operationRangeSet) && !isBookingMidSelection && !isOperationMidSelection) {
            fetchReport();
        }
        // eslint-disable-next-line
    }, [dateRange, operationDateRange, returnPremiseIds, currentPremiseIds, srcHubIds, destinationHubIds, docTypes, bookingTypes, awbNumbers, pagination.current, pagination.pageSize, isInitialized]);


    const fetchReport = async () => {
        // Create new request with cancellation support
        const { signal, requestId } = createNewRequest("rto-with-reason-report");

        setLoading(true);
        try {
            const processedAwbNumbers = awbNumbers ? awbNumbers.split(",").map(val => val.trim()).filter(Boolean) : undefined;
            // Resolve Current Hubs (currentPremiseIds) for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedCurrentPremiseIds = currentPremiseIds?.includes("ALL")
                ? activeOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : currentPremiseIds;

            // Prepare params according to API requirements
            const params = {
                ...(returnPremiseIds?.length > 0 && { returnPremiseIds }),
                ...(resolvedCurrentPremiseIds?.length > 0 && { currentPremiseIds: resolvedCurrentPremiseIds }),
                ...(srcHubIds?.length > 0 && { srcHubIds }),
                ...(destinationHubIds?.length > 0 && { destinationHubIds }),
                ...(docTypes?.length > 0 && { docTypes }),
                ...(bookingTypes?.length > 0 && { bookingTypes }),
                ...(processedAwbNumbers && { awbNumbers: processedAwbNumbers.join(",") }),
                ...(dateRange && dateRange[0] && dateRange[1] && {
                    bookingCompleteTimeFrom: dateRange[0].format("YYYY-MM-DD"),
                    bookingCompleteTimeTo: dateRange[1].format("YYYY-MM-DD"),
                }),
                ...(operationDateRange && operationDateRange[0] && operationDateRange[1] && {
                    currentOperationTimeFrom: operationDateRange[0].format("YYYY-MM-DD"),
                    currentOperationTimeTo: operationDateRange[1].format("YYYY-MM-DD"),
                }),
                page: pagination.current - 1,
                size: pagination.pageSize
            };

            const reportResponse = await ReportsService.getRtoWithReasonReport(params, { signal });
            const { data, total, lastRefreshedAt, nextScheduledRefresh: nextRefresh } = reportResponse;

            // Check if this is still the latest request
            if (!isLatestRequest(requestId)) {
                return;
            }

            setReportData(data);
            setLastsynced(lastRefreshedAt);
            setNextScheduledRefresh(nextRefresh);
            setPagination(prev => ({
                ...prev,
                total,
            }));
        } catch (error) {
            if (!isLatestRequest(requestId)) return;
            // Ignore cancellation errors
            if (error.name === "AbortError" || error.name === "CanceledError" || error.code === "ERR_CANCELED") {
                return;
            }

            console.error("Error fetching report data:", error);
            // Show error to user if it's not a cancellation
            message.error(error.message || "Failed to fetch report data");

            setReportData([]);
            setPagination(prev => ({ ...prev, total: 0 }));
        } finally {
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };


    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        try {
            if ((!dateRange || !dateRange[0] || !dateRange[1]) && (!operationDateRange || !operationDateRange[0] || !operationDateRange[1])) {
                message.error("Please select at least one date range for download");
                return;
            }

            const processedAwbNumbers = awbNumbers ? awbNumbers.split(",").map(val => val.trim()).filter(Boolean) : undefined;

            const downloadParams = {
                ...(dateRange && dateRange[0] && dateRange[1] && {
                    bookingCompleteTimeFrom: dateRange[0].format("YYYY-MM-DD"),
                    bookingCompleteTimeTo: dateRange[1].format("YYYY-MM-DD"),
                }),
                ...(operationDateRange && operationDateRange[0] && operationDateRange[1] && {
                    currentOperationTimeFrom: operationDateRange[0].format("YYYY-MM-DD"),
                    currentOperationTimeTo: operationDateRange[1].format("YYYY-MM-DD"),
                }),
                // Resolve Current Hubs (currentPremiseIds) for Download logic
                ...(() => {
                    const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
                    const activeOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
                    const resolvedCurrentPremiseIds = currentPremiseIds?.includes("ALL")
                        ? activeOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                        : currentPremiseIds;
                    return resolvedCurrentPremiseIds?.length > 0 ? { currentPremiseIds: resolvedCurrentPremiseIds } : {};
                })(),
                returnPremiseIds: returnPremiseIds.length > 0 ? returnPremiseIds : undefined,
                srcHubIds: srcHubIds.length > 0 ? srcHubIds : undefined,
                destinationHubIds: destinationHubIds.length > 0 ? destinationHubIds : undefined,
                docTypes: docTypes.length > 0 ? docTypes : undefined,
                bookingTypes: bookingTypes.length > 0 ? bookingTypes : undefined,
                awbNumbers: processedAwbNumbers
            };

            await downloadRtoWithReasonReport(downloadParams);
            message.success("Download started successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to start download");
        }
    };

    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 70,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "AWB Number",
            dataIndex: "awbNumber",
            key: "awbNumber",
            width: 150,
            render: (text) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        { title: "Booking Date", dataIndex: "bookingCompleteTime", key: "bookingCompleteTime", width: 150, render: (text) => text ? moment(text).format("YYYY-MM-DD") : "---" },
        { title: "Last Status Date", dataIndex: "currentOperationTime", key: "currentOperationTime", width: 180, render: (text) => text || "---" },
        { title: "Last Status", dataIndex: "status", key: "status", width: 180, render: (text) => text || "---" },
        { title: "Reason", dataIndex: "rtoReason", key: "rtoReason", width: 200, render: (text) => text || "---" },
        { title: "Current Hub", dataIndex: "currentPremiseName", key: "currentPremiseName", width: 180, render: (text) => text || "---" },
        { title: "Source Hub", dataIndex: "srcHubName", key: "srcHubName", width: 180, render: (text) => text || "---" },
        { title: "Destination Hub", dataIndex: "destinationHubName", key: "destinationHubName", width: 180, render: (text) => text || "---" },
        { title: "Doc Type", dataIndex: "docType", key: "docType", width: 100, render: (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : "---" },
        { title: "Booking Type", dataIndex: "bookingType", key: "bookingType", width: 120, render: (text) => text || "---" },
    ];

    const docTypeOptions = [
        { label: "DOX", value: "DOX" },
        { label: "NON-DOX", value: "NON-DOX" },
    ];

    const bookingTypeOptions = [
        { label: "COURIER", value: "COURIER" },
        { label: "CARGO", value: "CARGO" },
        { label: "INTERNATIONAL", value: "INTERNATIONAL" },
        { label: "ACCOUNT", value: "ACCOUNT" },
        { label: "E-COMM", value: "E-COMM" },
        { label: "CASH", value: "CASH" },
        { label: "LTL", value: "LTL" },
        { label: "FTL", value: "FTL" },

    ];

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="RTO Shipment with Reason Report">
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking Date Range</div>
                        <CalendarComponent
                            type="range"
                            value={dateRange}
                            onChange={(val) => {
                                setDateRange(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Last Status Date Range</div>
                        <CalendarComponent
                            type="range"
                            value={operationDateRange}
                            onChange={(val) => {
                                setOperationDateRange(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Return Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Return Premise"
                            options={hubList}
                            onChange={(val) => {
                                setReturnPremiseIds(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={returnPremiseIds}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            loading={hubListLoading}
                            showSearch={true}
                            optionFilterProp="label"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Current Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Current Premise"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setCurrentPremiseIds, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={currentPremiseIds}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            loading={hubListLoading}
                            showSearch={true}
                            optionFilterProp="label"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Source Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Source Hub"
                            options={hubList}
                            onChange={(val) => {
                                setSrcHubIds(val || []);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={srcHubIds}
                            allowClear={false}
                            mode="multiple"
                            loading={hubListLoading}
                            showSearch={true}
                            optionFilterProp="label"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            disabled={false}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Destination Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Destination Hub"
                            options={hubList}
                            onChange={(val) => {
                                setDestinationHubIds(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={destinationHubIds}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            loading={hubListLoading}
                            showSearch={true}
                            optionFilterProp="label"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Doc Type</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Doc Type"
                            options={docTypeOptions}
                            onChange={(val) => {
                                setDocTypes(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={docTypes}
                            allowClear
                            mode="multiple"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking Type</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Booking Type"
                            options={bookingTypeOptions}
                            onChange={(val) => {
                                setBookingTypes(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={bookingTypes}
                            allowClear
                            mode="multiple"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Tracking Id</div>
                        <Input
                            className="custom-input"
                            placeholder="Search Tracking Id"
                            value={awbNumbers}
                            onChange={(e) => {
                                setAwbNumbers(e.target.value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">&nbsp;</div>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={handleDownload}
                            disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                            style={{ width: "100%" }}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
            </Card>
            <Spin spinning={loading}>
                <TableComponent
                    columns={columns}
                    dataSource={reportData}
                    loading={false}
                    pagination={pagination}
                    onChange={handleTableChange}
                    scroll={{ y: "calc(100vh - 350px)", x: "max-content" }}
                    sticky
                />
            </Spin>
        </div>
    );
};

export default RtoWithReasonReport;
